import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * The whole control — box plus label — is the Radix root, so the label text is
 * the accessible name, the click target is the full width, and there is exactly
 * one focus ring. Indeterminate is drawn with a dash, not a fainter tick: the
 * shape has to say "partial" on a monochrome screen too.
 *
 * The box stays 16px everywhere, because a row of oversized checkboxes down the
 * left of a table is the loudest thing on the screen. On a touch device an
 * invisible 44px pseudo-element carries the hit area instead, so the target
 * grows without the box growing with it.
 */
export function Checkbox(p: {
  checked: boolean | 'indeterminate'
  onCheckedChange: (v: boolean) => void
  label?: string
  className?: string
  disabled?: boolean
  'aria-label'?: string
}) {
  const isIndeterminate = p.checked === 'indeterminate'

  return (
    <CheckboxPrimitive.Root
      checked={p.checked}
      disabled={p.disabled}
      aria-label={p['aria-label']}
      onCheckedChange={(next) => p.onCheckedChange(next === true)}
      className={cn(
        'group relative inline-flex min-h-8 items-center gap-2.5 rounded-sm text-left',
        'disabled:cursor-not-allowed disabled:opacity-50',
        "[@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:top-1/2",
        "[@media(pointer:coarse)]:before:left-1/2 [@media(pointer:coarse)]:before:-translate-x-1/2",
        "[@media(pointer:coarse)]:before:-translate-y-1/2",
        "[@media(pointer:coarse)]:before:h-[max(100%,44px)] [@media(pointer:coarse)]:before:w-[max(100%,44px)]",
        "[@media(pointer:coarse)]:before:content-['']",
        p.className
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-4 flex-none items-center justify-center rounded-xs border bg-s1',
          'border-line-strong text-accent-fg transition-colors duration-100',
          'group-hover:border-accent/70',
          'group-data-[state=checked]:border-accent group-data-[state=checked]:bg-accent',
          'group-data-[state=indeterminate]:border-accent group-data-[state=indeterminate]:bg-accent'
        )}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
          {isIndeterminate ? (
            <Minus className="size-3" strokeWidth={3} />
          ) : (
            <Check className="size-3" strokeWidth={3.25} />
          )}
        </CheckboxPrimitive.Indicator>
      </span>

      {p.label ? (
        <span className="truncate text-[13.5px] text-fg select-none">{p.label}</span>
      ) : null}
    </CheckboxPrimitive.Root>
  )
}
